import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom/client';
import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import './styles/global.css';
import { routeTree } from './routeTree.gen';
import { supabase } from './lib/supabase';
import { UpdatePrompt } from './components/UpdatePrompt';
import {
  initializeOneSignal,
  loginOneSignalUser,
  logoutOneSignalUser,
} from './lib/services/onesignal';
import type { User } from '@supabase/supabase-js';

// Create a client for React Query with sensible defaults
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30 * 1000, // 30 seconds - data is fresh for 30 seconds
      gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
      refetchOnMount: true, // Refetch on mount if stale (default behavior)
      refetchOnWindowFocus: true, // Refetch on window focus if stale (good for real-time data)
      refetchOnReconnect: true, // Refetch on reconnect
    },
  },
});

// Create router once - it will be updated via context prop
const router = createRouter({
  routeTree,
  context: {
    auth: {
      user: null,
      isAuthenticated: false,
    },
    queryClient,
  },
});

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();
  const oneSignalInitialized = useRef(false);
  const prevUserRef = useRef<User | null | undefined>(undefined);

  // Navigate after auth state changes, once context has updated
  useEffect(() => {
    // Skip first render (undefined means we haven't tracked yet)
    if (prevUserRef.current !== undefined) {
      // User was logged in and is now logged out
      if (prevUserRef.current !== null && user === null) {
        router.navigate({ to: '/login' });
      }
      // User was logged out and is now logged in
      else if (prevUserRef.current === null && user !== null) {
        // Only force navigation after sign-in if we're on the login page.
        // This preserves deep-link URLs on cold app loads.
        if (window.location.pathname === '/login') {
          const redirectTo =
            new URLSearchParams(window.location.search).get('redirect') || '/';
          router.navigate({ to: redirectTo });
        }
      }
    }
    prevUserRef.current = user;
  }, [user]);

  // Initialize OneSignal once on app mount
  useEffect(() => {
    if (!oneSignalInitialized.current) {
      oneSignalInitialized.current = true;
      initializeOneSignal();
    }
  }, []);

  useEffect(() => {
    // Get initial session with error handling
    supabase.auth
      .getSession()
      .then(({ data: { session }, error }) => {
        if (error) {
          console.error('Error getting session:', error);
        }
        const sessionUser = session?.user ?? null;
        setUser(sessionUser);

        // Identify user in PostHog if already logged in
        if (sessionUser) {
          posthog?.identify(sessionUser.id, {
            email: sessionUser.email,
          });
          // Link OneSignal user
          loginOneSignalUser(sessionUser.id);
        }

        setLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching session:', error);
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const sessionUser = session?.user ?? null;
      setUser(sessionUser);

      // Handle PostHog identification based on auth event
      if (event === 'SIGNED_IN' && sessionUser) {
        posthog?.identify(sessionUser.id, {
          email: sessionUser.email,
        });
        // Link OneSignal user on sign in
        loginOneSignalUser(sessionUser.id);
      } else if (event === 'SIGNED_OUT') {
        posthog?.reset();
        // Logout OneSignal user on sign out
        logoutOneSignalUser();
      }

      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, [posthog]);

  // Show loading state while checking auth
  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <RouterProvider
      router={router}
      context={{
        auth: {
          user,
          isAuthenticated: !!user,
        },
        queryClient,
      }}
    />
  );
}

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: ReturnType<typeof createRouter<typeof routeTree>>;
  }
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <PostHogProvider
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_KEY}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        defaults: '2025-05-24',
        capture_exceptions: true, // This enables capturing exceptions using Error Tracking, set to false if you don't want this
        debug: import.meta.env.MODE === 'development',
      }}
    >
      <QueryClientProvider client={queryClient}>
        <App />
        <UpdatePrompt />
      </QueryClientProvider>
    </PostHogProvider>
  </React.StrictMode>
);
